/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import { BugOutlined, CopyOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/es/table';
import React, { useEffect, useMemo, useState } from 'react';

import { PaginatedReleasesResponse, ReleaseFilters, ReleaseResponse } from '@/client';
import { GuardedButton } from '@/components/GuardedButton';
import MimicHighlighter from '@/components/MimicHighlighter';
import { PaginatedTable } from '@/components/PaginatedTable';
import { RelativeTime } from '@/components/RelativeTime';
import { useMessage } from '@/hooks/message';
import { usePublishReleaseMutation } from '@/hooks/mutation/usePublishReleaseMutation';
import { useRevokeReleaseMutation } from '@/hooks/mutation/useRevokeReleaseMutation';
import { MimicTranslationFunction, useMimicTranslation } from '@/hooks/useMimicTranslation';
import { Flex } from '@/primitives/Flex';
import { OsIcon } from '@/primitives/Icons';
import { Text } from '@/primitives/Text';
import { Tooltip } from '@/primitives/Tooltip';
import { mimicEnv } from '@/utils/environments';
import { UpdateParams } from '@/utils/params';

import { InstallerDownloadIcon } from './InstallerDownloadIcon';

function useModifierKey(key: 'metaKey' | 'ctrlKey' | 'altKey' | 'shiftKey') {
  const [isPressed, setIsPressed] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e[key]) setIsPressed(true);
    };
    const handleKeyUp = (e: KeyboardEvent) => {
      if (!e[key]) setIsPressed(false);
    };
    const handleBlur = () => setIsPressed(false);

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    window.addEventListener('blur', handleBlur);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('blur', handleBlur);
    };
  }, [key]);

  return isPressed;
}

export type ReleaseManagementTableProps = {
  releasesData: PaginatedReleasesResponse;
  publishedReleaseIds: Set<string>;
  releaseFilters: ReleaseFilters;
  updateQueryParams: UpdateParams<ReleaseResponse, Partial<ReleaseFilters>>;
  tenantId: string;
  primaryAction?: React.ReactNode;
};

export function ReleaseManagementTable({
  releasesData,
  publishedReleaseIds,
  updateQueryParams,
  releaseFilters,
  tenantId,
  primaryAction,
}: ReleaseManagementTableProps) {
  const { t } = useMimicTranslation('releaseManagement');
  const [message] = useMessage();
  const isMetaPressed = useModifierKey('metaKey');
  const isDevEnvironment = ['dev', 'qa', 'local'].includes(mimicEnv());
  const [mutatingReleaseId, setMutatingReleaseId] = useState<string | null>(null);
  const [hoveredRowId, setHoveredRowId] = useState<string | null>(null);
  const [mutationStartTime, setMutationStartTime] = useState<number | null>(null);
  const [optimisticPublished, setOptimisticPublished] = useState<Set<string>>(new Set());
  const [optimisticUnpublished, setOptimisticUnpublished] = useState<Set<string>>(new Set());

  const MIN_LOADING_DURATION = 400; // minimum ms to show loading state

  const clearLoadingWithMinDuration = (callback: () => void) => {
    const elapsed = mutationStartTime ? Date.now() - mutationStartTime : MIN_LOADING_DURATION;
    const remaining = Math.max(0, MIN_LOADING_DURATION - elapsed);

    setTimeout(() => {
      setMutatingReleaseId(null);
      setMutationStartTime(null);
      callback();
    }, remaining);
  };

  // Combine actual published IDs with optimistic updates
  const effectivePublishedIds = useMemo(() => {
    const result = new Set(publishedReleaseIds);
    optimisticPublished.forEach((id) => result.add(id));
    optimisticUnpublished.forEach((id) => result.delete(id));
    return result;
  }, [publishedReleaseIds, optimisticPublished, optimisticUnpublished]);

  const publishMutation = usePublishReleaseMutation({
    onSuccess: (_, variables) => {
      clearLoadingWithMinDuration(() => {
        message.success(t('publishSuccess'));
        // Optimistically mark as published
        setOptimisticPublished((prev) => new Set(prev).add(variables.releaseId));

        setOptimisticUnpublished((prev) => {
          const next = new Set(prev);
          next.delete(variables.releaseId);
          return next;
        });
      });
    },
    onError: () => {
      clearLoadingWithMinDuration(() => {
        message.error(t('publishError'));
      });
    },
  });

  const revokeMutation = useRevokeReleaseMutation({
    onSuccess: (_, variables) => {
      clearLoadingWithMinDuration(() => {
        message.success(t('unpublishSuccess'));
        // Optimistically mark as unpublished
        setOptimisticUnpublished((prev) => new Set(prev).add(variables.releaseId));

        setOptimisticPublished((prev) => {
          const next = new Set(prev);
          next.delete(variables.releaseId);
          return next;
        });
      });
    },
    onError: () => {
      clearLoadingWithMinDuration(() => {
        message.error(t('unpublishError'));
      });
    },
  });

  const handlePublish = (release: ReleaseResponse) => {
    setMutatingReleaseId(release.id);
    setMutationStartTime(Date.now());

    publishMutation.mutate({
      releaseId: release.id,
      tenantIds: [tenantId],
    });
  };

  const handleUnpublish = (release: ReleaseResponse) => {
    setMutatingReleaseId(release.id);
    setMutationStartTime(Date.now());

    revokeMutation.mutate({
      releaseId: release.id,
      tenantIds: [tenantId],
    });
  };

  const handleCopyReleaseId = (releaseId: string) => {
    navigator.clipboard.writeText(releaseId);
    message.success(t('copiedReleaseId'));
  };

  const columns = getReleaseColumns(
    t,
    releaseFilters,
    effectivePublishedIds,
    handlePublish,
    handleUnpublish,
    mutatingReleaseId,
    isMetaPressed,
    isDevEnvironment,
    hoveredRowId,
    handleCopyReleaseId,
  );

  return (
    <PaginatedTable
      data={releasesData}
      columns={columns}
      rowKey={(release) => release.id}
      data-testid="release-management-table"
      onParamsChange={updateQueryParams}
      primaryAction={primaryAction}
      onRow={(record) => ({
        onMouseEnter: () => setHoveredRowId((record as ReleaseResponse).id),
        onMouseLeave: () => setHoveredRowId(null),
      })}
    />
  );
}

function getReleaseColumns(
  t: MimicTranslationFunction<'releaseManagement'>,
  releaseFilters: ReleaseFilters,
  publishedReleaseIds: Set<string>,
  onPublish: (release: ReleaseResponse) => void,
  onUnpublish: (release: ReleaseResponse) => void,
  mutatingReleaseId: string | null,
  isMetaPressed: boolean,
  isDevEnvironment: boolean,
  hoveredRowId: string | null,
  onCopyReleaseId: (releaseId: string) => void,
): ColumnsType<ReleaseResponse> {
  return [
    {
      title: <span data-testid="releaseManagement.targetOs">{t('table.os')}</span>,
      dataIndex: 'targetOs',
      align: 'center',
      filters: [
        { text: 'Windows', value: 'windows' },
        { text: 'Linux', value: 'linux' },
      ],
      render: (targetOs: 'windows' | 'linux') => {
        const osName = targetOs === 'windows' ? 'Windows' : 'Linux';
        return (
          <Tooltip title={osName}>
            <div data-testid="os-icon">
              <OsIcon name={targetOs} />
            </div>
          </Tooltip>
        );
      },
      width: '60px',
    },
    {
      title: <span data-testid="releaseManagement.version">{t('table.version')}</span>,
      dataIndex: 'version',
      sorter: true,
      filterMode: 'menu',
      render: (version: string, release: ReleaseResponse) => {
        const isPublished = publishedReleaseIds.has(release.id);
        const showDebugFeature = isDevEnvironment && isMetaPressed && isPublished;

        const handleDebugClick = () => {
          // eslint-disable-next-line no-console
          console.log('clicked');
        };

        const versionText = <MimicHighlighter searchText={releaseFilters?.version} text={version} />;

        const versionElement = showDebugFeature ? (
          <span
            role="button"
            tabIndex={0}
            onClick={handleDebugClick}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') handleDebugClick();
            }}
            style={{ cursor: 'pointer' }}
            data-testid="debug-version-button"
          >
            {versionText}
          </span>
        ) : (
          <span>{versionText}</span>
        );

        return (
          <Flex gap="xs" align="center">
            {showDebugFeature ? <Tooltip title={`ID: ${release.id}`}>{versionElement}</Tooltip> : versionElement}
            {release.buildNumber && (
              <Tooltip title={`Build ${release.buildNumber}`}>
                <span
                  style={{
                    fontSize: '10px',
                    backgroundColor: 'rgba(0, 0, 0, 0.06)',
                    borderRadius: '10px',
                    padding: '1px 6px',
                    color: 'rgba(0, 0, 0, 0.65)',
                    cursor: 'pointer',
                  }}
                >
                  +{release.buildNumber}
                </span>
              </Tooltip>
            )}
            {release.packageType && (
              <span
                style={{
                  fontSize: '10px',
                  backgroundColor: 'rgba(0, 0, 0, 0.06)',
                  borderRadius: '10px',
                  padding: '1px 6px',
                  color: 'rgba(0, 0, 0, 0.65)',
                }}
              >
                {release.packageType}
              </span>
            )}
            {release.debug && (
              <Tooltip title="Debug build">
                <BugOutlined style={{ fontSize: '12px', color: 'rgba(0, 0, 0, 0.65)', cursor: 'pointer' }} />
              </Tooltip>
            )}
            {publishedReleaseIds.has(release.id) && release.schemaVersion === 'v1' && (
              <InstallerDownloadIcon releaseId={release.id} />
            )}
            {hoveredRowId === release.id && (
              <Tooltip title={t('copyReleaseId')}>
                <CopyOutlined
                  data-testid="copy-release-id"
                  onClick={() => onCopyReleaseId(release.id)}
                  style={{
                    fontSize: '12px',
                    color: 'rgba(0, 0, 0, 0.45)',
                    cursor: 'pointer',
                  }}
                />
              </Tooltip>
            )}
          </Flex>
        );
      },
    },
    {
      title: <span data-testid="releaseManagement.schemaVersion">{t('table.schemaVersion')}</span>,
      dataIndex: 'schemaVersion',
      align: 'center',
      filters: [
        { text: 'v1', value: 'v1' },
        { text: 'v2', value: 'v2' },
      ],
      render: (schemaVersion: string) => {
        return <Text>{schemaVersion}</Text>;
      },
      width: '90px',
    },
    {
      title: <span data-testid="releaseManagement.architecture">{t('table.arch')}</span>,
      dataIndex: 'architecture',
      align: 'center',
      filterMode: 'menu',
      render: (architecture: string) => {
        return <Text>{architecture}</Text>;
      },
      width: '100px',
    },
    {
      title: <span data-testid="releaseManagement.dateReleased">{t('table.dateReleased')}</span>,
      dataIndex: 'dateReleased',
      sorter: true,
      render: (dateReleased: string) => {
        if (!dateReleased) return <Text>-</Text>;
        return <RelativeTime date={dateReleased} />;
      },
      width: '200px',
    },
    {
      title: <span data-testid="releaseManagement.actions">{t('table.actions')}</span>,
      dataIndex: 'actions',
      width: '140px',
      render: (_, release: ReleaseResponse) => {
        const isPublished = publishedReleaseIds.has(release.id);
        const isLoading = mutatingReleaseId === release.id;
        const buttonStyle = { width: '130px' };

        return isPublished ? (
          <GuardedButton
            requiredRole="superadmin"
            dataTestId="unpublish-release"
            type="default"
            onClick={() => onUnpublish(release)}
            loading={isLoading}
            style={buttonStyle}
          >
            {t('unpublish')}
          </GuardedButton>
        ) : (
          <GuardedButton
            requiredRole="superadmin"
            dataTestId="publish-release"
            type="primary"
            onClick={() => onPublish(release)}
            loading={isLoading}
            style={buttonStyle}
          >
            {t('publish')}
          </GuardedButton>
        );
      },
    },
  ];
}
